import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClipboardCheck, Truck, Home, Warehouse, Smartphone } from 'lucide-react';
import { useUser } from '../../shared/context/UserContext';
import { useLauncherContext } from '../../shared/hooks/useLauncherContext';
import { getVisibleSections } from '../../shared/nav/sections';
import { BUILD_SHA, BUILD_DIRTY } from '../../buildInfo';
import './AppShell.css';

/* ── Section icons (18x18, white stroke, 1.5px) ─────────────────────── */
// Existing sections use hand-tuned inline SVG. New placeholder sections use
// lucide-react icons (already a dependency) for consistency with the pages
// they navigate to.
const SECTION_ICONS = {
  digitization: <ClipboardCheck size={18} strokeWidth={1.5} />,
  disposal: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="12" height="2" rx="0.5"/>
      <rect x="5" y="8" width="8" height="2" rx="0.5"/>
      <rect x="7" y="12" width="4" height="2" rx="0.5"/>
    </svg>
  ),
  lifecycle: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="6" height="6" rx="1" fill="currentColor" fillOpacity="0.3"/>
      <rect x="10" y="2" width="6" height="6" rx="1"/>
      <rect x="2" y="10" width="6" height="6" rx="1"/>
      <rect x="10" y="10" width="6" height="6" rx="1"/>
    </svg>
  ),
  moves: <Truck size={18} strokeWidth={1.5} />,
  warehouse: <Warehouse size={18} strokeWidth={1.5} />,
  field: <Smartphone size={18} strokeWidth={1.5} />,
  admin: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="6" r="3"/>
      <path d="M3 16c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
    </svg>
  ),
};

/* ── Breadcrumb mapping ──────────────────────────────────────────────── */
const ROUTE_BREADCRUMBS = {
  '/digitization/registry':  { section: 'Digitization', page: 'Asset Registry' },
  '/digitization/inventory': { section: 'Digitization', page: 'Field Inventory' },
  '/digitization':          { section: '',                 page: 'Digitization' },
  '/disposal/inventory':    { section: 'Disposal Review',  page: 'Field Inventory' },
  '/disposal/recon':        { section: 'Disposal Review',  page: 'Disposal Recon' },
  '/disposal/portal':       { section: 'Disposal Review',  page: 'Disposal Portal' },
  '/lifecycle/inventory':   { section: 'Lifecycle Review',  page: 'Field Inventory' },
  '/lifecycle/recon':       { section: 'Lifecycle Review',  page: 'Lifecycle Recon' },
  '/moves':                 { section: '',                 page: 'Tool Moves' },
  '/warehouse/items':       { section: 'Warehouse',        page: 'Stored Items' },
  '/warehouse':             { section: '',                 page: 'Warehouse' },
  '/admin/list-assignment': { section: 'Admin', page: 'List Assignment' },
  '/admin/user-management': { section: 'Admin', page: 'Users' },
  '/admin/site-management': { section: 'Admin', page: 'Sites' },
  '/admin/projects':        { section: 'Admin', page: 'Projects' },
  '/admin/data-ops':        { section: 'Admin', page: 'Data Operations' },
  '/admin/data':            { section: 'Admin', page: 'Data Exchange' },
  '/admin/disposal':        { section: 'Admin', page: 'Scrap Projects' },
  '/admin/dispositions':    { section: 'Admin', page: 'Dispositions' },
};

function getBreadcrumb(pathname, lastSection) {
  // Exact match first
  for (const [route, bc] of Object.entries(ROUTE_BREADCRUMBS)) {
    if (pathname === route || pathname.startsWith(route + '/')) return bc;
  }
  // /registry is shared — use last active section context
  if (pathname === '/registry' || pathname.startsWith('/registry/')) {
    const sectionLabel = lastSection === 'lifecycle' ? 'Lifecycle Review' : 'Disposal Review';
    return { section: sectionLabel, page: 'Asset Registry' };
  }
  return { section: '', page: '' };
}

/* ── Font-size preference ────────────────────────────────────────────── */
const FONT_SCALES = { S: '87.5%', M: '100%', L: '112.5%' };

/* ═══════════════════════════════════════════════════════════════════════ */

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isInventoryRoute = /\/(disposal|lifecycle)\/inventory\/.+/.test(location.pathname);
  const { userProfile, callerEmail } = useUser();
  const role = userProfile?.role;
  const isAdmin = role === 'ADMIN';
  const visibleSections = getVisibleSections({ role });
  const isLauncherContext = useLauncherContext();

  const [fontScale, setFontScale] = useState(() => localStorage.getItem('tmg_font_scale') || 'M');

  useEffect(() => {
    const pct = FONT_SCALES[fontScale] || FONT_SCALES.M;
    document.documentElement.style.fontSize = pct;
    localStorage.setItem('tmg_font_scale', fontScale);
  }, [fontScale]);

  const [openSection, setOpenSection] = useState(null); // 'disposal' | 'lifecycle' | 'admin' | null
  const [lastSection, setLastSection] = useState('disposal'); // for /registry breadcrumb
  const [mobileOpen, setMobileOpen] = useState(false);
  const flyoutRef = useRef(null);
  const railRef = useRef(null);

  // Active rail section — state-driven so clicks highlight immediately,
  // before navigation completes. useEffect below re-syncs on pathname change
  // (back button, breadcrumb, external navigation).
  const [activeSectionKey, setActiveSectionKey] = useState(() => {
    const p = location.pathname;
    if (p.startsWith('/digitization')) return 'digitization';
    if (p.startsWith('/moves')) return 'moves';
    if (p.startsWith('/warehouse')) return 'warehouse';
    if (p.startsWith('/admin')) return 'admin';
    if (p.startsWith('/lifecycle')) return 'lifecycle';
    if (p.startsWith('/disposal')) return 'disposal';
    return 'disposal'; // /registry at mount defaults to disposal (matches lastSection default)
  });

  // Re-sync active section when pathname changes (back button, breadcrumb, etc.)
  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith('/digitization')) setActiveSectionKey('digitization');
    else if (p.startsWith('/moves')) setActiveSectionKey('moves');
    else if (p.startsWith('/warehouse')) setActiveSectionKey('warehouse');
    else if (p.startsWith('/admin')) setActiveSectionKey('admin');
    else if (p.startsWith('/lifecycle')) setActiveSectionKey('lifecycle');
    else if (p.startsWith('/disposal')) setActiveSectionKey('disposal');
    else if (p === '/registry' || p.startsWith('/registry/')) setActiveSectionKey(lastSection);
  }, [location.pathname, lastSection]);

  // Track last non-admin section for /registry breadcrumb
  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith('/disposal')) setLastSection('disposal');
    else if (p.startsWith('/lifecycle')) setLastSection('lifecycle');
  }, [location.pathname]);

  // Close flyout on navigation
  useEffect(() => { setOpenSection(null); setMobileOpen(false); }, [location.pathname]);

  // Close flyout on outside click
  useEffect(() => {
    if (!openSection) return;
    function handleClick(e) {
      if (flyoutRef.current?.contains(e.target)) return;
      if (railRef.current?.contains(e.target)) return;
      setOpenSection(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openSection]);

  // Close flyout on Escape
  useEffect(() => {
    if (!openSection && !mobileOpen) return;
    function handleKey(e) {
      if (e.key === 'Escape') { setOpenSection(null); setMobileOpen(false); }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [openSection, mobileOpen]);

  function handleSectionClick(key) {
    const section = visibleSections.find(s => s.key === key);
    if (section?.directNavigate) {
      // No flyout for placeholder sections — close any open panel and navigate.
      setOpenSection(null);
      setActiveSectionKey(key);
      navigate(section.route);
      return;
    }
    setOpenSection(prev => prev === key ? null : key);
    setActiveSectionKey(key);
  }

  function handleItemClick(route, sectionKey) {
    // Update lastSection for /registry breadcrumb context
    if (sectionKey === 'disposal' || sectionKey === 'lifecycle') {
      setLastSection(sectionKey);
    }
    // Force navigation even if already at this route (e.g. /registry from two sections)
    navigate(route, { state: { section: sectionKey, t: Date.now() } });
    // Always close flyout — don't rely on pathname change
    setOpenSection(null);
  }

  const isRouteActive = useCallback((route) => {
    return location.pathname === route || location.pathname.startsWith(route + '/');
  }, [location.pathname]);

  /* ── User initials ──────────────────────────────────────────────────── */
  const displayName = userProfile?.display_name || '';
  const initials = displayName
    ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : (callerEmail || '?')[0].toUpperCase();

  const breadcrumb = getBreadcrumb(location.pathname, lastSection);
  const openSectionData = visibleSections.find(s => s.key === openSection);

  /* ── Dynamic browser tab title ─────────────────────────────────────── */
  useEffect(() => {
    document.title = breadcrumb.page ? `ToolSight \u00b7 ${breadcrumb.page}` : 'ToolSight';
  }, [breadcrumb.page]);

  return (
    <div className={`appshell${isInventoryRoute ? ' inventory-route' : ''}`}>
      {/* ── Mobile backdrop ────────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Rail (always 52px) ────────────────────────────────────────── */}
      <nav className={`rail ${mobileOpen ? 'mobile-open' : ''}`} ref={railRef}>
        {/* Logo */}
        <div className="rail-logo">
          <div className="tmg-square">
            <svg width="22" height="22" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="30,3 53,16 53,44 30,57 7,44 7,16" stroke="#1D9E75" strokeWidth="3"/>
              <circle cx="30" cy="30" r="11" stroke="#1D9E75" strokeWidth="2.2"/>
              <circle cx="30" cy="30" r="4" fill="#1D9E75"/>
            </svg>
          </div>
        </div>

        {/* Section icons */}
        <div className="rail-sections">
          {visibleSections.filter(s => !s.adminOnly).map(section => (
            <div
              key={section.key}
              className={`rail-icon ${activeSectionKey === section.key ? 'active' : ''} ${openSection === section.key ? 'open' : ''}`}
              onClick={() => handleSectionClick(section.key)}
              data-tooltip={section.label}
            >
              {SECTION_ICONS[section.key]}
            </div>
          ))}
        </div>

        {/* Admin icon pinned to bottom */}
        {isAdmin && (
          <div className="rail-bottom">
            <div
              className={`rail-icon ${activeSectionKey === 'admin' ? 'active' : ''} ${openSection === 'admin' ? 'open' : ''}`}
              onClick={() => handleSectionClick('admin')}
              data-tooltip="Admin"
            >
              {SECTION_ICONS.admin}
            </div>
          </div>
        )}

        {/* User avatar */}
        <div className="rail-footer">
          <div className="user-avatar">{initials}</div>
        </div>
      </nav>

      {/* ── Flyout panel ──────────────────────────────────────────────── */}
      <div
        className={`flyout ${openSection ? 'open' : ''}`}
        ref={flyoutRef}
      >
        {openSectionData && (
          <>
            <div className="flyout-header">{openSectionData.label}</div>
            <div className="flyout-items">
              {openSectionData.items.map(item => (
                <div
                  key={item.route}
                  className={`flyout-item ${isRouteActive(item.route) ? 'active' : ''}`}
                  onClick={() => handleItemClick(item.route, openSectionData.key)}
                >
                  <span className="flyout-dot" />
                  <span className="flyout-label">{item.label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Topbar ────────────────────────────────────────────────────── */}
      <header className="topbar">
        <button className="topbar-hamburger" onClick={() => setMobileOpen(true)}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M3 5h14M3 10h14M3 15h14"/>
          </svg>
        </button>
        {isLauncherContext && (
          <button
            className="topbar-hamburger"
            onClick={() => navigate('/home')}
            aria-label="Home"
            title="Home"
          >
            <Home size={20} strokeWidth={1.5} />
          </button>
        )}
        <div className="topbar-breadcrumb">
          <span className="breadcrumb-section">ToolSight</span>
          {breadcrumb.section && (
            <>
              <span className="breadcrumb-sep">&rsaquo;</span>
              <span className="breadcrumb-section">{breadcrumb.section}</span>
            </>
          )}
          {breadcrumb.page && (
            <>
              <span className="breadcrumb-sep">&rsaquo;</span>
              <span className="breadcrumb-page">{breadcrumb.page}</span>
            </>
          )}
        </div>
        <div className="topbar-right">
          <div className="topbar-font-toggle">
            {['S', 'M', 'L'].map(size => (
              <button
                key={size}
                className={`topbar-font-btn${fontScale === size ? ' active' : ''}`}
                onClick={() => setFontScale(size)}
                title={`Font size: ${size === 'S' ? 'Small' : size === 'M' ? 'Medium' : 'Large'}`}
              >
                {size}
              </button>
            ))}
          </div>
          <div className="topbar-user-chip">
            <div className="topbar-user-avatar">{initials}</div>
            <div className="topbar-user-info">
              <span className="topbar-user-name">{displayName || callerEmail}</span>
              <span className="topbar-user-role">{role}</span>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <main className="appshell-main">
        <div className="appshell-main-content">{children}</div>
        <footer className="appshell-footer">
          <div className="appshell-footer-copyright">
            &copy; 2026 Tooling Management Group LLC
            <span className="appshell-footer-sep">&middot;</span>
            <span data-testid="appshell-build">
              {BUILD_SHA}{BUILD_DIRTY ? '+dirty' : ''}
            </span>
          </div>
          <a href="/legal/privacy">Privacy Notice</a>
          <span className="appshell-footer-sep">&middot;</span>
          <a href="/legal/terms">Terms of Use</a>
          <span className="appshell-footer-sep">&middot;</span>
          <a href="/legal/accessibility">Accessibility</a>
          <span className="appshell-footer-sep">&middot;</span>
          <a href="/legal/security">Security</a>
        </footer>
      </main>
    </div>
  );
}
